import React from 'react';
import { Link } from 'react-router-dom';
import { firePAEvent } from './utils/analytics';
import { SS_HERO } from './components/ProductVisuals';

/*
 * /for-employers — added 2 Sep 2026.
 *
 * The landing page's section 2 exit needed somewhere that expands the employer
 * case in employer words. It had been pointing at /cmpx#hr, which is one view
 * of a four-view product page — right content, wrong depth.
 *
 * The division of labour, so these do not drift into two employer pages:
 *   /                 why it matters. The spine.
 *   /for-employers    what you get, see and do. This page.
 *   /cmpx             the product itself, from all four points of view.
 * /cmpx#hr is now a summary that points here rather than carrying the detail.
 *
 * This is navigation-complete, not content-complete. Features expand here
 * later; the structure is what needed settling first.
 *
 * Claims: coverage and visibility are things we can demonstrate. No saving, no
 * outcome, no prevention. The cost figures live on /contact where every source
 * is shown, and the presenteeism multiples are still with CMA (ESC-W-01).
 */

const ForEmployersPage = () => (
    <div className="pa2">

        <section className="hero" id="top">
            <div className="wrap two">
                <div className="heromain">
                    <h1 className="on-dark">
                        <span className="setup">Cover everyone.</span>
                        <span className="payoff">Not just the people who ask.</span>
                    </h1>
                    <p className="lede">
                        Musculoskeletal Health, MSK Health, is one of the biggest causes of lost
                        working days. Every person you provision gets a physiotherapy programme
                        personalised by a registered physiotherapist, from day one.
                    </p>
                    <p className="nerve">
                        You see how much of it is actually happening. Not whether people signed up.
                    </p>
                    <div className="cta-row">
                        <Link
                            className="btn btn-t"
                            to="/contact?who=employer"
                            onClick={() => firePAEvent('employer_cta_click', { source: 'for_employers_hero' })}
                        >
                            See what MSK Health costs you
                        </Link>
                    </div>
                </div>

                <div className="herovis">
                    <div className="dash" aria-label="CMPX employer view, illustrative">
                        <div className="d-top"><i /><i /><i /><span className="w">CMPX · employer view</span></div>
                        <div className="d-body">
                            <div className="d-side"><i className="on" /><i /><i /><i /><i /><i /></div>
                            <div className="d-main">
                                <div className="d-h"><span className="t">Workforce MSK Health</span><span className="s">Whole-workforce level</span></div>
                                <div className="d-tiles">
                                    <div><div className="v">128<small> / 150</small></div><div className="c">covered of workforce</div></div>
                                    <div><div className="v g">72%</div><div className="c">participating</div></div>
                                    <div><div className="v g">8 wks</div><div className="c">reporting window</div></div>
                                </div>
                                <div className="d-chart">
                                    <div className="cl">Adherence, last 8 weeks</div>
                                    <svg viewBox="0 0 320 74" width="100%" height="74" aria-hidden="true">
                                        <polyline points="0,62 45,54 90,57 135,42 180,36 225,27 270,22 320,15" fill="none" stroke="#4CB6C3" strokeWidth="3.4" strokeLinecap="round" />
                                        <polyline points="0,66 45,64 90,65 135,62 180,61 225,60 270,59 320,58" fill="none" stroke="#cbd6e0" strokeWidth="2.2" strokeDasharray="5 5" />
                                    </svg>
                                </div>
                                <div className="d-foot"><span>No group under 50 shown</span><span>Export · CSV, PDF</span></div>
                            </div>
                        </div>
                    </div>
                    <p className="cap">Illustrative example. Not a customer result.</p>
                </div>
            </div>
        </section>

        {/* Moved ahead of #see on 7 Sep. The hero promises to cover everyone, and
            the reader's next question is what they are covered WITH — not what the
            reporting looks like. Showing the artefact first also gives the privacy
            contrast below something concrete to be protecting.

            Reworked 7 Sep. The page argued coverage and reporting and never showed
            the buyer what their people actually receive, so they were being asked
            to fund something they had never seen. The programme screen is now
            beside the copy, and the closing paragraph is the argument the page was
            missing: measurement and care are the same act here, which is why the
            numbers are a by-product rather than a survey.

            Reworked rather than added, so the page stays at four sections. The
            three points are unchanged in substance, moved from cards to bullets
            because a two-column row cannot hold a card grid. */}
        <section className="tint" id="people">
            <div className="wrap">
                <div className="featrow rev">
                    <div className="ftext">
                        <h2>
                            <span className="setup">What lands</span>
                            <span className="payoff">on their phone.</span>
                        </h2>
                        <p className="lede">
                            Personal to them: a registered physiotherapist writes it from how
                            that person actually moves, and it changes as they do. It runs on
                            their phone, around their day, with nothing to log and no end date.
                        </p>
                        <ul className="fpts">
                            <li>No referral, no waiting list, and no time off to attend</li>
                            <li>Theirs to keep, even if they leave you</li>
                            <li>A named clinician, with their HCPC number shown</li>
                        </ul>
                    </div>
                    <div className="fvis">
                        {SS_HERO}
                        <p className="cap">Illustrative. Not a real programme.</p>
                    </div>
                </div>

                <p className="quiet">
                    You are not buying a benefit nobody opens. The same thing that makes it
                    useful to them is what makes it measurable for you: because the programme
                    runs on their movement, take-up and adherence are a by-product of the care
                    rather than a survey about it. They get{' '}
                    <Link to="/steadystride">an app and a physiotherapist</Link>; you get{' '}
                    <Link to="/cmpx#roles">a reporting view</Link>, and nothing that identifies
                    anyone.
                </p>
            </div>
        </section>

        <section className="white" id="see">
            <div className="wrap">
                <h2>
                    <span className="setup">Reported against your workforce.</span>
                    <span className="payoff">Not against your sign-ups.</span>
                </h2>
                <p className="lede">
                    Coverage is committed against the people you provision and reported against
                    your whole workforce, so the gap between the two is visible and is yours to
                    act on.
                </p>

                <div className="contrast">
                    <div className="cc yes">
                        <h3>You see</h3>
                        <ul>
                            <li>How many you cover, against total workforce</li>
                            <li>What proportion are participating</li>
                            <li>Aggregate adherence over time</li>
                            <li>Exports for board and benefits reporting</li>
                        </ul>
                    </div>
                    <div className="cc no">
                        <h3>You never see</h3>
                        <ul>
                            <li>Who has enrolled, by name</li>
                            <li>Anyone&rsquo;s symptoms, programme or activity</li>
                            <li>Anything said to a physiotherapist</li>
                            <li>Any group smaller than fifty people</li>
                        </ul>
                    </div>
                </div>

                <p className="quiet">
                    That boundary is what makes the service usable by your staff.{' '}
                    <Link to="/trust-centre">Trust centre</Link> sets it out in full, including
                    who controls the data and what happens when someone leaves.
                </p>
            </div>
        </section>

        <section className="s-promise" id="start">
            <div className="wrap">
                <h2 className="on-dark">
                    <span className="setup">Compare with</span>
                    <span className="payoff">the published benchmarks.</span>
                </h2>
                <p className="lede">
                    ONS and HSE figures, applied to your headcount, with every source shown and
                    the method open to check. Then talk to us.
                </p>
                <div className="cta-row">
                    <Link
                        className="btn btn-t"
                        to="/contact?who=employer"
                        onClick={() => firePAEvent('employer_cta_click', { source: 'for_employers_close' })}
                    >
                        See what MSK Health costs you
                    </Link>
                </div>
                {/* The third question a benefits buyer asks, after what their people
                    get and what they can see: what does it cost me to run. The page
                    had no answer, and buyers who have been burned by a platform that
                    needed constant chasing will assume the worst.

                    Kept to a line here rather than a fifth section: the page's job is
                    to get to a conversation, and rollout detail belongs in one. Every
                    claim is one the site already makes elsewhere — provisioning, an
                    invitation with a code, and reporting that comes from movement
                    rather than from asking people. No timeline is given, because
                    nobody has committed to one. */}
                <p className="quiet">
                    Running it is a list and an invitation. We provision the people you cover,
                    they enter a code, and the reporting fills itself in, so nobody is chasing
                    anyone for a survey response.
                </p>
            </div>
        </section>

    </div>
);

export default ForEmployersPage;
